import moment from 'moment'

// The group message sender's "time available" window. The pages used to re-parse it as
// 'MM-DD-YY hh:mm', which drops the am/pm and turns every afternoon into the same hour that
// morning, so an afternoon window searched the wrong half of the day (FI-2169).
//
// Four-digit year: with 'YY' a date outside 1969-2068 round-tripped to the wrong century in
// silence -- 2125 was stored as '25' and read back as 2025 (FI-2169 review).
export const PICKER_FORMAT = 'MM-DD-YYYY hh:mm a'

// how LONG a window may be. the booking lookup behind "available" buffers a week around the window,
// so a longer window can miss assignments.
export const MAX_WINDOW_DAYS = 7

// how far AHEAD a window may start. this is a different limit: the booking lookup's buffer is
// relative to the window, so a four hour window next spring reads the same bookings as one today.
// Anderson has 4,659 bookings starting beyond next week, out to Dec 2027, and sales quoting a trip
// needs to reach them (#1725).
export const MAX_WINDOW_HORIZON_DAYS = 365

export function parse_window_value(value) {
  if (!value)
    return null
  // a moment (the initial value) passes through; a string needs the full format, am/pm included
  const parsed = moment.isMoment(value) ? value.clone() : moment(value, PICKER_FORMAT)
  return parsed.isValid() ? parsed : null
}

export function window_value_to_iso(value) {
  const parsed = parse_window_value(value)
  return parsed ? parsed.format('YYYY-MM-DDTHH:mm:ssZ') : ''
}

export function format_window_value(value) {
  const parsed = parse_window_value(value)
  return parsed ? parsed.format(PICKER_FORMAT) : ''
}

// the window is entered as a date and a time, in separate controls: a dispatcher picking cover for
// tonight thinks "today, 6pm to midnight", not in datetime strings. these convert between the two.
export function window_date(value) {
  const parsed = parse_window_value(value)
  return parsed ? parsed.format('YYYY-MM-DD') : ''
}

export function window_time(value) {
  const parsed = parse_window_value(value)
  return parsed ? parsed.format('HH:mm') : ''
}

export function compose_window_value(date, time) {
  if (!date || !time)
    return ''
  const parsed = moment(date + ' ' + time, 'YYYY-MM-DD HH:mm')
  return parsed.isValid() ? parsed.format(PICKER_FORMAT) : ''
}

// an end at or before the start on the same date means they meant the small hours of the next day
// (6pm to 2am). the end date control moves with it, so the roll is visible rather than implied.
export function roll_overnight(start, end) {
  const from = parse_window_value(start)
  const to = parse_window_value(end)
  if (!from || !to || to.isAfter(from))
    return end
  return to.add(1, 'day').format(PICKER_FORMAT)
}

// a plain-language echo of the window, so nobody has to read two date fields to check it
export function window_summary(start, end) {
  const from = parse_window_value(start)
  const to = parse_window_value(end)
  if (!from || !to || !to.isAfter(from))
    return ''
  const hours = to.diff(from, 'minutes') / 60
  // pluralize off the number actually printed: 1.03 rounds to 1 and used to read "1 hours"
  const rounded = Math.round(hours * 10) / 10
  const length = hours < 1 ? Math.round(hours * 60) + ' min'
    : rounded + (rounded == 1 ? ' hour' : ' hours')
  const same_day = from.isSame(to, 'day')
  return from.format('ddd MMM D, h:mm a') + ' to ' +
    (same_day ? to.format('h:mm a') : to.format('ddd MMM D, h:mm a')) +
    ' (' + length + ')'
}

// the window the quick buttons set: [start, end] as picker-formatted strings
export function quick_window(name, now) {
  const from = now ? moment(now) : moment()
  switch (name) {
    case 'next_4h':
      return [from.clone(), from.clone().add(4, 'hours')]
    case 'rest_of_today':
      return [from.clone(), from.clone().endOf('day')]
    case 'tomorrow':
      return [from.clone().add(1, 'day').startOf('day'), from.clone().add(1, 'day').endOf('day')]
    default:
      return [from.clone(), from.clone().add(12, 'hours')]
  }
}

// end before start is the one mistake the picker can't prevent, and it silently returns nobody
export function window_error(start, end, now) {
  const from = parse_window_value(start)
  const to = parse_window_value(end)
  if (!from || !to)
    return 'enter a start and end time'
  if (!to.isAfter(from))
    return 'the end time has to come after the start time'
  if (to.diff(from, 'days', true) > MAX_WINDOW_DAYS)
    return 'the window can cover at most ' + MAX_WINDOW_DAYS + ' days'
  // min/max on a date input only mark it invalid, they do not stop the value reaching us, so the
  // horizon has to be enforced here or a mistyped year goes to the booking lookup (FI-2169 review)
  const today = now ? moment(now) : moment()
  if (from.isBefore(today.clone().startOf('day')))
    return 'the window cannot start in the past'
  if (from.diff(today, 'days', true) > MAX_WINDOW_HORIZON_DAYS)
    return 'the window cannot start more than ' + MAX_WINDOW_HORIZON_DAYS + ' days from now'
  return ''
}
